import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, SquarePen } from 'lucide-react'
import { Button } from '../ui/button'
import { WorkoutList } from './WorkoutList'
import { DaySelectorRow } from './DaySelectorRow'
import { AddWorkoutDialog } from './AddWorkoutDialog'
import { CreateCustomExerciseDialog } from './CreateCustomExerciseDialog'
import type { Exercise } from '../../types/exercise'
import type { Workout } from '../../types/workout'

interface UnscheduledWorkoutsSectionProps {
  workouts: Workout[]
  weekStart: string
  scheduleMode: boolean
  onDayPick: (workoutId: string, date: string | null) => void
  onReorder: (ids: string[]) => void
  onCreate: (exerciseIds: string[]) => Promise<void>
}

export function UnscheduledWorkoutsSection({
  workouts,
  weekStart,
  scheduleMode,
  onDayPick,
  onReorder,
  onCreate,
}: UnscheduledWorkoutsSectionProps) {
  const { t } = useTranslation()
  const [addOpen, setAddOpen] = useState(false)
  const [customOpen, setCustomOpen] = useState(false)

  // A suggestion added straight from the custom dialog lands in this same section.
  async function handleScheduleSuggestion(exercise: Exercise) {
    await onCreate([exercise.id])
  }

  return (
    <div className="rounded-xl border border-dashed p-3 flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-muted-foreground">
          {t('trainingDiary.scheduleMode.unscheduled.title', 'Unscheduled')}
        </span>
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs border-dashed px-2"
            onClick={() => setAddOpen(true)}
            aria-label={t('trainingDiary.scheduleMode.unscheduled.addButton', 'Add unscheduled workout')}
            disabled={scheduleMode}
          >
            <Plus className="size-3 mr-0.5" />
            {t('trainingDiary.workouts.addButtonShort', 'workout')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs border-dashed px-2"
            onClick={() => setCustomOpen(true)}
            aria-label={t('trainingDiary.customExercise.addButton', 'Create a custom workout')}
            disabled={scheduleMode}
          >
            <SquarePen className="size-3 mr-0.5" />
            {t('trainingDiary.customExercise.addButtonShort', 'custom')}
          </Button>
        </div>
      </div>

      {workouts.length > 0 ? (
        <WorkoutList
          workouts={workouts}
          hideResults
          sortable={!scheduleMode}
          onReorder={onReorder}
          renderFooter={(w) =>
            scheduleMode ? (
              <DaySelectorRow
                weekStart={weekStart}
                selectedDate={null}
                onSelect={(date) => onDayPick(w.id, date)}
              />
            ) : null
          }
        />
      ) : (
        <p className="text-xs text-muted-foreground italic">
          {t('trainingDiary.scheduleMode.unscheduled.empty', 'No unscheduled workouts. Add one to start planning the week.')}
        </p>
      )}

      <AddWorkoutDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onConfirm={onCreate}
      />

      <CreateCustomExerciseDialog
        open={customOpen}
        onOpenChange={setCustomOpen}
        onSchedule={handleScheduleSuggestion}
      />
    </div>
  )
}
